if (!window.customElements.get('custom-subscribe-accordion-picker')) {
  window.customElements.define(
    'custom-subscribe-accordion-picker',
    class CustomSubscribeAccordionPicker extends window.HTMLElement {
      connectedCallback() {
        this.initDOMElements();
        this.boundHandleChange = this.handleChange.bind(this);
        this.boundHandleKeydown = this.handleKeydown.bind(this);
        this.boundHandleAddToCart = this.handleAddToCart.bind(this);
        this.boundHandleBuyOnce = this.handleBuyOnce.bind(this);
        this.initEventListeners();
        this.updateUI();
      }

      disconnectedCallback() {
        this.removeEventListeners();
      }

      initDOMElements() {
        this.form = this.querySelector('form');
        this.cards = this.querySelectorAll('.subscribe-accordion-picker__card');
        this.addToCartButton = this.querySelector('#add-to-cart-button');
        this.addToCartPrice = this.querySelector('#add-to-cart-subscription-price');
        this.addToCartCompareAtPrice = this.querySelector('#add-to-cart-compare-at-price');
        this.addToCartCompareSep = this.querySelector('#add-to-cart-compare-sep');
        this.buyOnceLink = this.querySelector('#subscribe-accordion-buy-once');
        this.enableGtmTracking = this.dataset.enableGtmTracking === 'true';
        this.checkoutMode = this.dataset.checkoutMode || 'external';
        this.shopifyRedirect = this.dataset.shopifyRedirect || 'drawer';
        this.isAddingToCart = false;
      }

      initEventListeners() {
        this.form?.addEventListener('change', this.boundHandleChange);
        this.cards.forEach((card) => {
          card.addEventListener('keydown', this.boundHandleKeydown);
        });

        if (this.checkoutMode === 'shopify') {
          this.addToCartButton?.addEventListener('click', this.boundHandleAddToCart);

          if (this.buyOnceLink?.tagName === 'BUTTON') {
            this.buyOnceLink.addEventListener('click', this.boundHandleBuyOnce);
          }
        }
      }

      removeEventListeners() {
        this.form?.removeEventListener('change', this.boundHandleChange);
        this.cards.forEach((card) => {
          card.removeEventListener('keydown', this.boundHandleKeydown);
        });
        this.addToCartButton?.removeEventListener('click', this.boundHandleAddToCart);
        this.buyOnceLink?.removeEventListener('click', this.boundHandleBuyOnce);
      }

      handleChange() {
        this.updateUI();
      }

      handleKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }

        const radio = event.currentTarget.querySelector('input[type="radio"]');

        if (!radio) {
          return;
        }

        event.preventDefault();
        radio.checked = true;
        this.updateUI();
      }

      /**
       * Main ATC — adds the selected option's variant WITH its
       * subscription selling plan (falls back to one-time if the
       * variant has no selling plans).
       */
      handleAddToCart(event) {
        event.preventDefault();

        const selectedVariant = this.getSelectedVariant();

        if (!selectedVariant) {
          return;
        }

        const { variantId, sellingPlanId, freeVariantIds } = selectedVariant.dataset;
        this.addToCart(
          variantId,
          sellingPlanId,
          this.addToCartButton,
          this.parseVariantIds(freeVariantIds),
        );
      }

      /**
       * Buy once — adds the same variant WITHOUT a selling plan
       * (one-time purchase).
       */
      handleBuyOnce(event) {
        event.preventDefault();

        const selectedVariant = this.getSelectedVariant();

        if (!selectedVariant) {
          return;
        }

        const { variantId } = selectedVariant.dataset;
        this.addToCart(variantId, null, this.buyOnceLink, []);
      }

      async addToCart(variantId, sellingPlanId, triggerElement, freeVariantIds = []) {
        if (this.isAddingToCart) {
          return;
        }

        if (!variantId) {
          console.error('[subscribe-accordion-picker] No variant ID set. Select a product in the block settings.');
          return;
        }

        this.setLoadingState(true, triggerElement);

        try {
          const rootUrl = window.Shopify?.routes?.root || '/';
          const paidVariantId = Number(variantId);
          const item = { id: paidVariantId, quantity: 1 };

          if (sellingPlanId) {
            item.selling_plan = Number(sellingPlanId);
          }

          // Add the qualifying item first, followed by configured one-time gift variants.
          // The existing Shopify Buy X Get Y discount will set eligible gifts to free.
          const uniqueGiftIds = [...new Set(freeVariantIds)]
            .map(Number)
            .filter((id) => Number.isInteger(id) && id > 0 && id !== paidVariantId);

          const items = [
            item,
            ...uniqueGiftIds.map((id) => ({
              id,
              quantity: 1,
              properties: { _automatic_free_gift: 'true' },
            })),
          ];

          const response = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ items }),
          });

          if (!response.ok) {
            const errorBody = await response.json().catch(() => null);
            throw new Error(errorBody?.description || `Cart add failed (${response.status})`);
          }

          if (this.shopifyRedirect === 'drawer') {
            await this.refreshAndOpenCartDrawer();
            this.setLoadingState(false, triggerElement);
          } else {
            const target = this.shopifyRedirect === 'cart' ? `${rootUrl}cart` : `${rootUrl}checkout`;
            window.location.assign(target);
          }
        } catch (error) {
          console.error('[subscribe-accordion-picker]', error);
          this.setLoadingState(false, triggerElement);
        }
      }

      /**
       * Impact theme (Maestrooo) integration:
       * 1. Fetch the latest cart state
       * 2. Dispatch the events Impact listens to so the drawer contents re-render
       * 3. Open the <cart-drawer> element (with fallbacks)
       */
      async refreshAndOpenCartDrawer() {
        const rootUrl = window.Shopify?.routes?.root || '/';
        let cart = null;

        try {
          const cartResponse = await fetch(`${rootUrl}cart.js`, {
            headers: { Accept: 'application/json' },
          });
          cart = await cartResponse.json();
        } catch (error) {
          console.error('[subscribe-accordion-picker] Could not fetch cart state', error);
        }

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true }),
        );

        if (cart) {
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail: { cart, baseEvent: 'variant:add' },
            }),
          );
        }

        window.requestAnimationFrame(() => {
          this.openCartDrawer();
        });
      }

      openCartDrawer() {
        const drawer =
          document.querySelector('cart-drawer') || document.getElementById('cart-drawer');

        if (drawer) {
          if (typeof drawer.show === 'function') {
            drawer.show();
            return;
          }

          if ('open' in drawer) {
            drawer.open = true;
            return;
          }

          drawer.setAttribute('open', '');
          return;
        }

        const cartToggle = document.querySelector(
          '[aria-controls="cart-drawer"], a[href$="/cart"][aria-controls]',
        );
        cartToggle?.click();
      }

      setLoadingState(isLoading, triggerElement) {
        this.isAddingToCart = isLoading;

        const target = triggerElement || this.addToCartButton;

        if (!target) {
          return;
        }

        target.classList.toggle('is-loading', isLoading);

        if (isLoading) {
          target.setAttribute('aria-busy', 'true');
          target.setAttribute('disabled', 'disabled');
        } else {
          target.removeAttribute('aria-busy');
          target.removeAttribute('disabled');
        }
      }

      updateUI() {
        const selectedVariant = this.getSelectedVariant();

        if (!selectedVariant) {
          return;
        }

        this.updateCardStates(selectedVariant);
        this.updateMainButton(selectedVariant);
        this.updateBuyOnceLink(selectedVariant);
      }

      updateCardStates(selectedVariant) {
        this.cards.forEach((card) => {
          const radio = card.querySelector('input[type="radio"]');
          const isSelected = radio === selectedVariant;

          card.classList.toggle('is-selected', isSelected);
          card.classList.toggle('is-collapsed', !isSelected);
          card.setAttribute('aria-expanded', isSelected ? 'true' : 'false');
        });
      }

      updateMainButton(selectedVariant) {
        const data = selectedVariant.dataset;

        if (
          this.checkoutMode !== 'shopify' &&
          this.addToCartButton?.tagName === 'A' &&
          data.checkoutUrl
        ) {
          this.addToCartButton.href = this.appendTrackingParams(data.checkoutUrl);
        }

        if (this.addToCartPrice) {
          this.addToCartPrice.textContent = data.ctaPrice || '';
        }

        if (this.addToCartCompareAtPrice) {
          const comparePrice = data.ctaComparePrice || '';

          if (comparePrice && comparePrice !== data.ctaPrice) {
            this.addToCartCompareAtPrice.textContent = comparePrice;
            this.addToCartCompareAtPrice.classList.remove('hidden');
            this.addToCartCompareSep?.classList.remove('hidden');
          } else {
            this.addToCartCompareAtPrice.classList.add('hidden');
            this.addToCartCompareSep?.classList.add('hidden');
          }
        }
      }

      updateBuyOnceLink(selectedVariant) {
        if (!this.buyOnceLink) {
          return;
        }

        const data = selectedVariant.dataset;
        const buyOnceText = data.buyOnceText || '';
        const buyOnceUrl = data.buyOnceUrl || '';

        this.buyOnceLink.textContent = buyOnceText;

        if (this.buyOnceLink.tagName === 'BUTTON') {
          // Shopify mode: visibility depends on text only (no URL needed)
          this.buyOnceLink.classList.toggle('hidden', !buyOnceText);
          return;
        }

        if (this.buyOnceLink.tagName === 'A') {
          if (buyOnceUrl) {
            this.buyOnceLink.href = this.appendTrackingParams(buyOnceUrl);
            this.buyOnceLink.classList.remove('hidden');
          } else {
            this.buyOnceLink.classList.add('hidden');
          }
        }
      }

      parseVariantIds(value) {
        if (!value) {
          return [];
        }

        return value
          .split(',')
          .map((id) => Number(id.trim()))
          .filter((id) => Number.isInteger(id) && id > 0);
      }

      getSelectedVariant() {
        return this.form?.querySelector('input[type="radio"]:checked');
      }

      appendTrackingParams(url) {
        if (!this.enableGtmTracking || !url || !window.SeranovaTracking) {
          return url;
        }

        return window.SeranovaTracking.appendParams(url);
      }
    },
  );
}